import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import billingService from './billingService';
// Credits are surfaced by the dashboard/stats fetch and echoed on scan
// responses, so we hydrate our balance from those existing thunks rather than
// adding a dedicated round-trip.
import {
  getDashboardStats,
  checkCompliance,
  scanContent,
  createJob,
} from '../contentCompliance/contentComplianceSlice';

// Cache the balance so the header pill shows a number instantly on reload,
// before dashboard/stats returns. Authoritative value always comes from the API.
const CACHED_BALANCE_KEY = 'cleanStudioCreditBalance';
const loadCachedBalance = () => {
  try {
    const raw = localStorage.getItem(CACHED_BALANCE_KEY);
    if (raw == null) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  } catch (e) {
    return null;
  }
};
const cacheBalance = (balance) => {
  try {
    if (balance == null) localStorage.removeItem(CACHED_BALANCE_KEY);
    else localStorage.setItem(CACHED_BALANCE_KEY, String(balance));
  } catch (e) {
    /* storage unavailable — pill just won't pre-fill */
  }
};

// Map the backend `credits` block (snake_case) to our camelCase shape.
const mapCredits = (c) => {
  if (!c) return null;
  return {
    creditBalance: c.credit_balance ?? 0,
    lifetimeCreditsPurchased: c.lifetime_credits_purchased ?? 0,
    autoRefillEnabled: !!c.auto_refill_enabled,
    autoRefillPack: c.auto_refill_pack ?? null,
    autoRefillThreshold: c.auto_refill_threshold ?? null,
    hasPaymentMethod: !!c.has_payment_method,
    enforcementMode: c.enforcement_mode ?? 'off',
  };
};

// Pull a fresh balance out of a scan response's echoed credit_usage, if present.
// Success responses use `credit_balance`; be tolerant of shape.
const balanceFromScanPayload = (payload) => {
  const usage = payload?.data?.credit_usage || payload?.credit_usage;
  if (!usage) return null;
  const bal = usage.credit_balance ?? usage.credits_balance;
  return typeof bal === 'number' ? bal : null;
};

export const createCheckout = createAsyncThunk(
  'billing/createCheckout',
  async ({ packId }, { getState, rejectWithValue }) => {
    try {
      const authToken = getState().auth.token;
      const response = await billingService.createCheckoutSession(packId, authToken);
      if (response.success && response.data?.checkout_url) {
        return response.data;
      }
      return rejectWithValue(response.message || 'Failed to start checkout');
    } catch (error) {
      const msg = error.response?.data?.message || error.message || 'Failed to start checkout';
      return rejectWithValue(msg);
    }
  }
);

export const fetchScanCost = createAsyncThunk(
  'billing/fetchScanCost',
  async ({ contentId }, { getState, rejectWithValue }) => {
    try {
      const authToken = getState().auth.token;
      const response = await billingService.getScanCost(contentId, authToken);
      if (response.success) {
        return response.data;
      }
      return rejectWithValue(response.message || 'Failed to get scan cost');
    } catch (error) {
      const msg = error.response?.data?.message || error.message || 'Failed to get scan cost';
      return rejectWithValue(msg);
    }
  }
);

const initialState = {
  credits: null, // {creditBalance, lifetimeCreditsPurchased, autoRefill*, hasPaymentMethod, enforcementMode}
  cachedBalance: loadCachedBalance(), // instant-render fallback for the pill

  checkoutStatus: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  checkoutError: null,

  scanCost: null, // last fetched {ready, credits_required, credits_balance, sufficient, band_label, ...}
  scanCostStatus: 'idle',
  scanCostError: null,

  // Out-of-credits modal (set when a scan is rejected with 402).
  outOfCredits: null, // {creditsRequired, creditsBalance, bandLabel} | null
};

const setBalance = (state, balance) => {
  if (balance == null) return;
  if (state.credits) state.credits.creditBalance = balance;
  state.cachedBalance = balance;
  cacheBalance(balance);
};

const billingSlice = createSlice({
  name: 'billing',
  initialState,
  reducers: {
    clearScanCost: (state) => {
      state.scanCost = null;
      state.scanCostStatus = 'idle';
      state.scanCostError = null;
    },
    // Called from the scan flow when a 402 InsufficientCreditsError is caught.
    showOutOfCredits: (state, action) => {
      state.outOfCredits = action.payload;
    },
    dismissOutOfCredits: (state) => {
      state.outOfCredits = null;
    },
    clearCheckoutStatus: (state) => {
      state.checkoutStatus = 'idle';
      state.checkoutError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Balance/prefs source of truth: the dashboard/stats fetch.
      .addCase(getDashboardStats.fulfilled, (state, action) => {
        const credits = mapCredits(action.payload?.data?.credits);
        if (credits) {
          state.credits = credits;
          state.cachedBalance = credits.creditBalance;
          cacheBalance(credits.creditBalance);
        }
      })

      // Keep the balance live after each scan (responses echo credit_usage).
      .addCase(checkCompliance.fulfilled, (state, action) => {
        setBalance(state, balanceFromScanPayload(action.payload));
      })
      .addCase(scanContent.fulfilled, (state, action) => {
        setBalance(state, balanceFromScanPayload(action.payload));
      })
      .addCase(createJob.fulfilled, (state, action) => {
        setBalance(state, balanceFromScanPayload(action.payload));
      })

      // Checkout
      .addCase(createCheckout.pending, (state) => {
        state.checkoutStatus = 'loading';
        state.checkoutError = null;
      })
      .addCase(createCheckout.fulfilled, (state) => {
        state.checkoutStatus = 'succeeded';
      })
      .addCase(createCheckout.rejected, (state, action) => {
        state.checkoutStatus = 'failed';
        state.checkoutError = action.payload || 'Failed to start checkout';
      })

      // Scan cost preview
      .addCase(fetchScanCost.pending, (state) => {
        state.scanCostStatus = 'loading';
        state.scanCostError = null;
      })
      .addCase(fetchScanCost.fulfilled, (state, action) => {
        state.scanCostStatus = 'succeeded';
        state.scanCost = action.payload;
        if (action.payload?.ready && typeof action.payload.credits_balance === 'number') {
          setBalance(state, action.payload.credits_balance);
        }
      })
      .addCase(fetchScanCost.rejected, (state, action) => {
        state.scanCostStatus = 'failed';
        state.scanCostError = action.payload || 'Failed to get scan cost';
      });
  },
});

export const {
  clearScanCost,
  showOutOfCredits,
  dismissOutOfCredits,
  clearCheckoutStatus,
} = billingSlice.actions;

// Selectors
export const selectCredits = (state) => state.billing.credits;
// Best available balance: live credits, else the cached fallback, else null.
export const selectCreditBalance = (state) =>
  state.billing.credits?.creditBalance ?? state.billing.cachedBalance ?? null;
export const selectEnforcementMode = (state) =>
  state.billing.credits?.enforcementMode ?? 'off';
export const selectCheckoutStatus = (state) => state.billing.checkoutStatus;
export const selectCheckoutError = (state) => state.billing.checkoutError;
export const selectScanCost = (state) => state.billing.scanCost;
export const selectScanCostStatus = (state) => state.billing.scanCostStatus;
export const selectOutOfCredits = (state) => state.billing.outOfCredits;

export default billingSlice.reducer;
